import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { connect, Provider, useSelector } from 'react-redux';
import { translate } from 'jitsi/base/i18n/functions';
import AbstractButton from 'jitsi/base/toolbox/components/AbstractButton';
import { IconPictureInPicture, IconMic, IconMicSlash, IconVideo, IconVideoOff, IconHangup, } from 'jitsi/base/icons/svg';
import Icon from 'jitsi/base/icons/components/Icon';
import { MEDIA_TYPE } from 'jitsi/base/media/constants';
import { muteLocal } from 'jitsi/video-menu/actions.any';
import { handleToggleVideoMuted } from 'jitsi/toolbox/actions.any';
import { leaveConference } from 'jitsi/base/conference/actions';
import { getTrackByMediaTypeAndParticipant, getLocalVideoTrack, isLocalTrackMuted, isRemoteTrackMuted } from 'jitsi/base/tracks/functions.web';
import { getAvatarColor, getInitials } from 'jitsi/base/avatar/functions';
import { isMobileBrowser } from 'jitsi/base/environment/utils';
export const isInIframe = () => {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
};
const drawRoundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
};
const drawVideoAspect = (ctx, video, dx, dy, dw, dh, mode) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh)
        return;
    const videoRatio = vw / vh;
    const targetRatio = dw / dh;
    let sx = 0;
    let sy = 0;
    let sw = vw;
    let sh = vh;
    if (mode === 'cover') {
        if (videoRatio > targetRatio) {
            sw = vh * targetRatio;
            sx = (vw - sw) / 2;
        }
        else {
            sh = vw / targetRatio;
            sy = (vh - sh) / 2;
        }
        ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
    }
    else {
        // contain mode
        let rw = dw;
        let rh = dh;
        let rx = dx;
        let ry = dy;
        if (videoRatio > targetRatio) {
            rh = dw / videoRatio;
            ry = dy + (dh - rh) / 2;
        }
        else {
            rw = dh * videoRatio;
            rx = dx + (dw - rw) / 2;
        }
        ctx.drawImage(video, rx, ry, rw, rh);
    }
};
const PipAvatar = ({ name, avatarUrl, size = 80, }) => {
    const initials = getInitials(name || 'P');
    const bgColor = getAvatarColor(initials, []);
    if (avatarUrl) {
        return (React.createElement("img", { src: avatarUrl, alt: name || 'Avatar', style: {
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid rgba(255, 255, 255, 0.25)',
            }, onError: (e) => {
                e.target.style.display = 'none';
            } }));
    }
    return (React.createElement("div", { style: {
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            backgroundColor: bgColor || '#10b981',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: `${Math.round(size * 0.4)}px`,
            letterSpacing: '1px',
            border: '2px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        } }, initials));
};
const VideoTrackView = ({ track, isLocal, domVideo, }) => {
    const videoRef = useRef(null);
    useEffect(() => {
        const el = videoRef.current;
        if (!el)
            return;
        // Helper to check if a MediaStream has active (non-ended) video tracks
        const hasLiveVideoTracks = (stream) => {
            if (!(stream instanceof MediaStream))
                return false;
            const vTracks = stream.getVideoTracks();
            return vTracks.length > 0 && vTracks.some((t) => t.readyState === 'live');
        };
        // 1. Prioritize track.jitsiTrack directly so switching between screenshare and camera is immediate and clean
        const jitsiTrack = track?.jitsiTrack || track;
        const mediaStreamTrack = jitsiTrack?.getTrack ? jitsiTrack.getTrack() : null;
        if (mediaStreamTrack && mediaStreamTrack.readyState === 'live') {
            try {
                const win = (el.ownerDocument?.defaultView || window);
                const StreamConstructor = win.MediaStream || MediaStream;
                const currentTracks = el.srcObject?.getVideoTracks?.();
                if (!currentTracks || currentTracks[0] !== mediaStreamTrack || currentTracks[0].readyState === 'ended') {
                    el.srcObject = new StreamConstructor([mediaStreamTrack]);
                    el.play?.().catch((e) => console.warn('[PiP] video.play() failed:', e));
                }
                return;
            }
            catch (e) {
                console.warn('[PiP] Error setting srcObject MediaStream:', e);
            }
        }
        // 2. If track has attach method
        if (jitsiTrack && typeof jitsiTrack.attach === 'function') {
            try {
                jitsiTrack.attach(el);
                return;
            }
            catch (e) {
                console.warn('[PiP] Error attaching jitsiTrack:', e);
            }
        }
        // 3. If track has direct stream
        if (track?.stream || jitsiTrack?.stream) {
            const stream = track.stream || jitsiTrack.stream;
            if (hasLiveVideoTracks(stream)) {
                try {
                    el.srcObject = stream;
                    el.play?.().catch((e) => console.warn('[PiP] video.play() fallback failed:', e));
                    return;
                }
                catch (e) {
                    console.warn('[PiP] Error setting srcObject fallback:', e);
                }
            }
        }
        // 4. Fallback to domVideo ONLY if it has live (non-ended) video tracks
        if (domVideo && hasLiveVideoTracks(domVideo.srcObject)) {
            el.srcObject = domVideo.srcObject;
            el.play?.().catch((e) => console.warn('[PiP] video.play() failed:', e));
            return;
        }
        return () => {
            if (el) {
                try {
                    el.srcObject = null;
                }
                catch (e) {
                    // ignore
                }
            }
        };
    }, [track, track?.jitsiTrack, track?.jitsiTrack?.getTrack?.(), track?.mediaType, track?.videoType, track?.muted, domVideo]);
    const isDesktop = track?.videoType === 'desktop' || track?.mediaType === 'screenshare';
    return (React.createElement("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, style: {
            width: '100%',
            height: '100%',
            objectFit: isDesktop ? 'contain' : 'cover',
            transform: isLocal && !isDesktop ? 'scaleX(-1)' : 'none',
            display: 'block',
        } }));
};
const PipWindowContent = ({ _audioMuted: propsAudioMuted, _videoMuted: propsVideoMuted, _localParticipant: propsLocalParticipant, _remoteParticipants: propsRemoteParticipants, _tracks: propsTracks, _largeVideoParticipantId: propsLargeVideoParticipantId, dispatch, pipWindow, }) => {
    const storeTracks = useSelector((state) => state['features/base/tracks'] || []);
    const _tracks = storeTracks.length > 0 ? storeTracks : propsTracks;
    const reduxAudioMuted = useSelector((state) => {
        const mediaState = state['features/base/media'];
        if (mediaState?.audio?.muted !== undefined) {
            return Boolean(mediaState.audio.muted);
        }
        const tracks = state['features/base/tracks'] || [];
        if (tracks.length > 0) {
            return isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
        }
        return propsAudioMuted;
    });
    const reduxVideoMuted = useSelector((state) => {
        const mediaState = state['features/base/media'];
        if (mediaState?.video?.muted !== undefined) {
            return Boolean(mediaState.video.muted);
        }
        const tracks = state['features/base/tracks'] || [];
        if (tracks.length > 0) {
            return isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
        }
        return propsVideoMuted;
    });
    const _audioMuted = typeof reduxAudioMuted === 'boolean' ? reduxAudioMuted : propsAudioMuted;
    const _videoMuted = typeof reduxVideoMuted === 'boolean' ? reduxVideoMuted : propsVideoMuted;
    const storeParticipantsState = useSelector((state) => state['features/base/participants']);
    const storeLargeVideoId = useSelector((state) => state['features/large-video']?.participantId);
    let _remoteParticipants = propsRemoteParticipants || [];
    if (storeParticipantsState?.remote) {
        try {
            _remoteParticipants = Array.from(storeParticipantsState.remote.values());
        }
        catch (e) {
            _remoteParticipants = propsRemoteParticipants || [];
        }
    }
    const _localParticipant = storeParticipantsState?.local || propsLocalParticipant;
    const _largeVideoParticipantId = storeLargeVideoId || propsLargeVideoParticipantId;
    // Filter out any virtual screenshare participants so we get the human participant
    const realRemoteParticipants = _remoteParticipants.filter((p) => !p.fakeParticipant || p.fakeParticipant !== 'remote-screenshare');
    const realLargeVideoOwnerId = _largeVideoParticipantId
        ? _largeVideoParticipantId.split('-')[0]
        : null;
    const remoteParticipant = (realLargeVideoOwnerId ? realRemoteParticipants.find((p) => p?.id === realLargeVideoOwnerId) : null) ||
        _remoteParticipants.find((p) => p?.id === _largeVideoParticipantId) ||
        realRemoteParticipants[0] ||
        _remoteParticipants[0];
    const hasRemoteParticipant = Boolean(remoteParticipant);
    // Check if there is an active screenshare from a remote participant
    const screenShareTrack = _tracks.find((t) => Boolean(t?.jitsiTrack) &&
        !t.local &&
        !t.muted &&
        (t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === 'desktop') &&
        (!remoteParticipant || t.participantId === remoteParticipant.id || t.participantId === realLargeVideoOwnerId)) || _tracks.find((t) => Boolean(t?.jitsiTrack) &&
        !t.local &&
        !t.muted &&
        (t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === 'desktop'));
    let remoteVideoTrack = null;
    if (screenShareTrack) {
        remoteVideoTrack = screenShareTrack;
    }
    else if (remoteParticipant) {
        // 1. Try finding video track for remoteParticipant.id
        remoteVideoTrack = getTrackByMediaTypeAndParticipant(_tracks, MEDIA_TYPE.VIDEO, remoteParticipant.id);
        // 2. Try realLargeVideoOwnerId if different
        if ((!remoteVideoTrack || remoteVideoTrack.muted) && realLargeVideoOwnerId && realLargeVideoOwnerId !== remoteParticipant.id) {
            remoteVideoTrack = getTrackByMediaTypeAndParticipant(_tracks, MEDIA_TYPE.VIDEO, realLargeVideoOwnerId);
        }
        // 3. Fallback to any non-local active camera video track
        if (!remoteVideoTrack || remoteVideoTrack.muted) {
            const anyCameraTrack = _tracks.find((t) => Boolean(t?.jitsiTrack) &&
                !t.local &&
                !t.muted &&
                t.mediaType === MEDIA_TYPE.VIDEO &&
                t.videoType !== 'desktop');
            if (anyCameraTrack) {
                remoteVideoTrack = anyCameraTrack;
            }
        }
    }
    const localVideoTrack = getLocalVideoTrack(_tracks) ||
        (_localParticipant?.id
            ? getTrackByMediaTypeAndParticipant(_tracks, MEDIA_TYPE.VIDEO, _localParticipant.id)
            : null);
    const domLocalVideo = document.getElementById('localVideo_container') ||
        document.getElementById('localVideo') ||
        document.getElementById('localScreenshare_container');
    const isLocalVideoActive = !_videoMuted &&
        ((Boolean(localVideoTrack) && !localVideoTrack?.muted) ||
            Boolean(domLocalVideo && domLocalVideo.readyState >= 1 && !domLocalVideo.paused));
    const domRemoteVideo = document.getElementById('largeVideo') ||
        document.querySelector('video[id^="remoteVideo_"]') ||
        (remoteParticipant?.id
            ? document.getElementById(`participant_${remoteParticipant.id}`)?.querySelector('video')
            : null);
    const isRemoteScreenShare = remoteVideoTrack?.videoType === 'desktop' ||
        remoteVideoTrack?.mediaType === MEDIA_TYPE.SCREENSHARE;
    const isRemoteTrackActive = Boolean(remoteVideoTrack) &&
        !remoteVideoTrack?.muted &&
        (typeof remoteVideoTrack.jitsiTrack?.isMuted !== 'function' || !remoteVideoTrack.jitsiTrack.isMuted()) &&
        (isRemoteScreenShare ||
            (remoteParticipant?.id ? !isRemoteTrackMuted(_tracks, MEDIA_TYPE.VIDEO, remoteParticipant.id) : true) ||
            (realLargeVideoOwnerId ? !isRemoteTrackMuted(_tracks, MEDIA_TYPE.VIDEO, realLargeVideoOwnerId) : true));
    const isRemoteVideoActive = isRemoteTrackActive;
    const remoteName = remoteParticipant?.name || remoteParticipant?.displayName || 'Remote Participant';
    const localName = _localParticipant?.name || _localParticipant?.displayName || 'Me';
    return (React.createElement("div", { style: {
            position: 'relative',
            width: '100vw',
            height: '100vh',
            backgroundColor: '#000000',
            overflow: 'hidden',
            boxSizing: 'border-box',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        } },
        hasRemoteParticipant ? (isRemoteVideoActive ? (React.createElement(VideoTrackView, { key: `remote-${remoteVideoTrack?.jitsiTrack?.getId?.() || remoteVideoTrack?.videoType || (isRemoteScreenShare ? 'screen' : 'camera')}`, track: remoteVideoTrack, domVideo: domRemoteVideo })) : (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: '#000000',
            } },
            React.createElement(PipAvatar, { name: remoteName, avatarUrl: remoteParticipant?.avatarURL, size: 84 }),
            React.createElement("div", { style: {
                    color: '#ffffff',
                    marginTop: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    letterSpacing: '0.2px',
                    maxWidth: '80%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                } }, remoteName)))) : isLocalVideoActive ? (React.createElement(VideoTrackView, { track: localVideoTrack, isLocal: true, domVideo: domLocalVideo })) : (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: '#000000',
            } },
            React.createElement(PipAvatar, { name: localName, avatarUrl: _localParticipant?.avatarURL, size: 84 }),
            React.createElement("div", { style: {
                    color: '#ffffff',
                    marginTop: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    letterSpacing: '0.2px',
                    maxWidth: '80%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                } }, localName))),
        hasRemoteParticipant && (React.createElement("div", { style: {
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '110px',
                height: '75px',
                borderRadius: '10px',
                border: '2px solid rgba(255, 255, 255, 0.25)',
                overflow: 'hidden',
                backgroundColor: '#000000',
                zIndex: 20,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            } }, isLocalVideoActive ? (React.createElement(VideoTrackView, { key: `local-${localVideoTrack?.jitsiTrack?.getId?.() || 'camera'}`, track: localVideoTrack, isLocal: true, domVideo: domLocalVideo })) : (React.createElement("div", { style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: '#000000',
            } },
            React.createElement(PipAvatar, { name: localName, avatarUrl: _localParticipant?.avatarURL, size: 36 }))))),
        React.createElement("div", { style: {
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '14px',
                padding: '6px 16px',
                borderRadius: '24px',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                backdropFilter: 'blur(8px)',
                zIndex: 100,
            } },
            React.createElement("button", { onClick: () => {
                    const nextAudioMuted = !_audioMuted;
                    if (typeof window.APP?.conference?.muteAudio === 'function') {
                        window.APP.conference.muteAudio(nextAudioMuted);
                    }
                    dispatch(muteLocal(nextAudioMuted, MEDIA_TYPE.AUDIO));
                }, title: _audioMuted ? 'Unmute Audio' : 'Mute Audio', style: {
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    color: '#1c2836',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                    padding: 0,
                    transition: 'all 0.2s ease',
                } },
                React.createElement(Icon, { src: _audioMuted ? IconMicSlash : IconMic, size: 22, color: '#1c2836' })),
            React.createElement("button", { onClick: () => {
                    dispatch(leaveConference());
                    if (pipWindow) {
                        pipWindow.close();
                    }
                }, title: 'End Call', style: {
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                    padding: 0,
                    transition: 'all 0.2s ease',
                } },
                React.createElement(Icon, { src: IconHangup, size: 22, color: '#ffffff' })),
            React.createElement("button", { onClick: () => {
                    const nextVideoMuted = !_videoMuted;
                    if (typeof window.APP?.conference?.muteVideo === 'function') {
                        window.APP.conference.muteVideo(nextVideoMuted, true);
                    }
                    dispatch(handleToggleVideoMuted(nextVideoMuted, true, true));
                }, title: _videoMuted ? 'Start Video' : 'Stop Video', style: {
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    color: '#1c2836',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                    padding: 0,
                    transition: 'all 0.2s ease',
                } },
                React.createElement(Icon, { src: _videoMuted ? IconVideoOff : IconVideo, size: 22, color: '#1c2836' })))));
};
class PipErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        console.error('[PiP Portal Error]', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (React.createElement("div", { style: { color: '#ffffff', padding: '20px', backgroundColor: '#1f2937', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' } },
                React.createElement("div", { style: { fontSize: '16px', fontWeight: 600, marginBottom: '8px' } }, "Picture-in-Picture Error"),
                React.createElement("div", { style: { fontSize: '12px', color: '#9ca3af' } }, String(this.state.error?.message || this.state.error))));
        }
        return this.props.children;
    }
}
let globalPipWindow = null;
let globalPipStoreUnsubscribe = null;
let globalPipCanvas = null;
let globalPipVideo = null;
let globalCompositeRemoteVideo = null;
let globalCompositeLocalVideo = null;
let globalAnimFrameId = null;
let globalWorker = null;
let globalIntervalId = null;
let globalIsCanvasCompositeRunning = false;
const startTicker = (callback) => {
    stopTicker();
    try {
        const workerBlob = new Blob([
            `let timer = null;
       self.onmessage = function(e) {
         if (e.data === 'start') {
           if (!timer) {
             timer = setInterval(function() {
               self.postMessage('tick');
             }, 33);
           }
         } else if (e.data === 'stop') {
           if (timer) {
             clearInterval(timer);
             timer = null;
           }
         }
       };`
        ], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(workerBlob);
        globalWorker = new Worker(workerUrl);
        globalWorker.onmessage = () => {
            callback();
        };
        globalWorker.postMessage('start');
    }
    catch (e) {
        console.warn('[PiP] Worker ticker creation failed:', e);
    }
    // Backup timer in main thread
    globalIntervalId = setInterval(callback, 33);
};
const stopTicker = () => {
    if (globalWorker) {
        try {
            globalWorker.postMessage('stop');
            globalWorker.terminate();
        }
        catch (e) { }
        globalWorker = null;
    }
    if (globalIntervalId) {
        clearInterval(globalIntervalId);
        globalIntervalId = null;
    }
};
class PictureInPictureButton extends AbstractButton {
    accessibilityLabel = 'toolbar.accessibilityLabel.pip';
    icon = IconPictureInPicture;
    label = 'toolbar.pip';
    toggledAccessibilityLabel = 'toolbar.accessibilityLabel.pip';
    tooltip = 'toolbar.pip';
    toggledLabel = 'toolbar.pipExit';
    toggledTooltip = 'toolbar.pipExit';
    constructor(props) {
        super(props);
        this.state = {
            pipActive: Boolean(document.pictureInPictureElement) || Boolean(globalPipWindow && !globalPipWindow.closed),
            pipWindow: globalPipWindow,
        };
        this._onPipChange = this._onPipChange.bind(this);
    }
    componentDidMount() {
        document.addEventListener('enterpictureinpicture', this._onPipChange, true);
        document.addEventListener('leavepictureinpicture', this._onPipChange, true);
        this._initPipVideo();
    }
    componentDidUpdate(prevProps) {
        if (this.state.pipActive && (prevProps._audioMuted !== this.props._audioMuted || prevProps._videoMuted !== this.props._videoMuted)) {
            this._syncMediaSessionState();
        }
    }
    componentWillUnmount() {
        document.removeEventListener('enterpictureinpicture', this._onPipChange, true);
        document.removeEventListener('leavepictureinpicture', this._onPipChange, true);
        // Do NOT stop canvas composite or clear mediaSession if PiP is actively running!
        // This allows the PiP window to stay active and streaming even when toolbar auto-hides.
        if (!document.pictureInPictureElement && (!globalPipWindow || globalPipWindow.closed)) {
            this._stopCanvasComposite();
            this._clearMediaSessionHandlers();
        }
    }
    _initPipVideo() {
        if (!globalPipCanvas) {
            globalPipCanvas = document.createElement('canvas');
            globalPipCanvas.width = 640;
            globalPipCanvas.height = 360;
            globalPipCanvas.style.position = 'fixed';
            globalPipCanvas.style.bottom = '0px';
            globalPipCanvas.style.right = '0px';
            globalPipCanvas.style.width = '10px';
            globalPipCanvas.style.height = '10px';
            globalPipCanvas.style.opacity = '0.001';
            globalPipCanvas.style.pointerEvents = 'none';
            globalPipCanvas.style.zIndex = '-9999';
            document.body.appendChild(globalPipCanvas);
            const ctx = globalPipCanvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#111827';
                ctx.fillRect(0, 0, 640, 360);
            }
        }
        if (!globalPipVideo) {
            globalPipVideo = document.createElement('video');
            globalPipVideo.muted = true;
            globalPipVideo.autoplay = true;
            globalPipVideo.playsInline = true;
            globalPipVideo.style.position = 'fixed';
            globalPipVideo.style.bottom = '0px';
            globalPipVideo.style.right = '0px';
            globalPipVideo.style.width = '10px';
            globalPipVideo.style.height = '10px';
            globalPipVideo.style.opacity = '0.001';
            globalPipVideo.style.pointerEvents = 'none';
            globalPipVideo.style.zIndex = '-9999';
            document.body.appendChild(globalPipVideo);
        }
        const stream = globalPipCanvas.captureStream ? globalPipCanvas.captureStream(30) : null;
        if (stream && globalPipVideo.srcObject !== stream) {
            globalPipVideo.srcObject = stream;
            globalPipVideo.play().catch(() => { });
        }
    }
    _stopCanvasComposite() {
        globalIsCanvasCompositeRunning = false;
        stopTicker();
        if (globalAnimFrameId) {
            cancelAnimationFrame(globalAnimFrameId);
            globalAnimFrameId = null;
        }
    }
    _onPipChange() {
        const isPip = Boolean(document.pictureInPictureElement) || Boolean(globalPipWindow && !globalPipWindow.closed);
        this.setState({
            pipActive: isPip,
            pipWindow: globalPipWindow,
        });
        if (isPip && document.pictureInPictureElement) {
            this._setupMediaSessionHandlers();
        }
        else if (!isPip) {
            this._stopCanvasComposite();
            this._clearMediaSessionHandlers();
        }
    }
    _clearMediaSessionHandlers() {
        if ('mediaSession' in navigator) {
            try {
                navigator.mediaSession.playbackState = 'none';
                navigator.mediaSession.setActionHandler('togglemicrophone', null);
                navigator.mediaSession.setActionHandler('togglecamera', null);
                navigator.mediaSession.setActionHandler('hangup', null);
            }
            catch (e) {
                // ignore
            }
        }
    }
    _syncMediaSessionState() {
        if ('mediaSession' in navigator) {
            try {
                const store = window.APP?.store;
                const state = store?.getState?.();
                const mediaState = state ? state['features/base/media'] : null;
                const audioMuted = mediaState?.audio ? Boolean(mediaState.audio.muted) : this.props._audioMuted;
                const videoMuted = mediaState?.video ? Boolean(mediaState.video.muted) : this.props._videoMuted;
                if (typeof navigator.mediaSession.setMicrophoneActive === 'function') {
                    navigator.mediaSession.setMicrophoneActive(!audioMuted);
                }
                if (typeof navigator.mediaSession.setCameraActive === 'function') {
                    navigator.mediaSession.setCameraActive(!videoMuted);
                }
            }
            catch (e) {
                // ignore
            }
        }
    }
    _setupMediaSessionHandlers() {
        if ('mediaSession' in navigator) {
            try {
                navigator.mediaSession.playbackState = 'playing';
                const store = window.APP?.store;
                const state = store?.getState?.();
                const participantsState = state ? state['features/base/participants'] : null;
                const remoteMap = participantsState?.remote;
                const remoteParticipant = remoteMap ? Array.from(remoteMap.values())[0] : null;
                const remoteName = remoteParticipant?.name || remoteParticipant?.displayName || '11sight Meeting';
                if (typeof window.MediaMetadata !== 'undefined') {
                    try {
                        navigator.mediaSession.metadata = new window.MediaMetadata({
                            title: '11sight Meeting',
                            artist: remoteName,
                        });
                    }
                    catch (e) {
                        // ignore
                    }
                }
                navigator.mediaSession.setActionHandler('togglemicrophone', () => {
                    const store = window.APP?.store;
                    const state = store?.getState?.();
                    const mediaState = state ? state['features/base/media'] : null;
                    const currentAudioMuted = mediaState?.audio ? Boolean(mediaState.audio.muted) : this.props._audioMuted;
                    const nextMuted = !currentAudioMuted;
                    if (typeof window.APP?.conference?.muteAudio === 'function') {
                        window.APP.conference.muteAudio(nextMuted);
                    }
                    const dispatch = store ? store.dispatch : this.props.dispatch;
                    dispatch(muteLocal(nextMuted, MEDIA_TYPE.AUDIO));
                    if (typeof navigator.mediaSession.setMicrophoneActive === 'function') {
                        navigator.mediaSession.setMicrophoneActive(!nextMuted);
                    }
                });
                navigator.mediaSession.setActionHandler('togglecamera', () => {
                    const store = window.APP?.store;
                    const state = store?.getState?.();
                    const mediaState = state ? state['features/base/media'] : null;
                    const currentVideoMuted = mediaState?.video ? Boolean(mediaState.video.muted) : this.props._videoMuted;
                    const nextMuted = !currentVideoMuted;
                    if (typeof window.APP?.conference?.muteVideo === 'function') {
                        window.APP.conference.muteVideo(nextMuted, true);
                    }
                    const dispatch = store ? store.dispatch : this.props.dispatch;
                    dispatch(handleToggleVideoMuted(nextMuted, true, true));
                    if (typeof navigator.mediaSession.setCameraActive === 'function') {
                        navigator.mediaSession.setCameraActive(!nextMuted);
                    }
                });
                navigator.mediaSession.setActionHandler('hangup', () => {
                    const store = window.APP?.store;
                    const dispatch = store ? store.dispatch : this.props.dispatch;
                    dispatch(leaveConference());
                });
                this._syncMediaSessionState();
            }
            catch (e) {
                console.warn('[PiP] MediaSession action handlers error:', e);
            }
        }
    }
    _getDocPipApi() {
        // 1. If running in an iframe, try the top-level window's documentPictureInPicture
        try {
            if (window.top && window.top !== window.self && typeof window.top.documentPictureInPicture !== 'undefined') {
                return window.top.documentPictureInPicture;
            }
        }
        catch (e) {
            // Cross-origin iframe
        }
        try {
            if (window.parent && window.parent !== window.self && typeof window.parent.documentPictureInPicture !== 'undefined') {
                return window.parent.documentPictureInPicture;
            }
        }
        catch (e) {
            // Cross-origin iframe
        }
        if (typeof window.documentPictureInPicture !== 'undefined') {
            return window.documentPictureInPicture;
        }
        return null;
    }
    _isToggled() {
        const docPip = this._getDocPipApi();
        const docPipWindow = docPip?.window;
        return Boolean(docPipWindow) || Boolean(document.pictureInPictureElement) || Boolean(globalPipWindow && !globalPipWindow.closed);
    }
    _getLabel() {
        return this._isToggled() ? 'toolbar.pipExit' : 'toolbar.pip';
    }
    _getTooltip() {
        return this._isToggled() ? 'toolbar.pipExit' : 'toolbar.pip';
    }
    _getAccessibilityLabel() {
        return this._isToggled() ? 'toolbar.pipExit' : 'toolbar.pip';
    }
    async _openDocumentPip() {
        try {
            if (globalPipWindow && !globalPipWindow.closed) {
                globalPipWindow.close();
                globalPipWindow = null;
                if (globalPipStoreUnsubscribe) {
                    globalPipStoreUnsubscribe();
                    globalPipStoreUnsubscribe = null;
                }
                this.setState({ pipWindow: null, pipActive: false });
                return true;
            }
            let pipWindow = null;
            // 1. Try Document Picture-in-Picture API
            const docPip = this._getDocPipApi();
            if (docPip && typeof docPip.requestWindow === 'function') {
                try {
                    if (docPip.window) {
                        docPip.window.close();
                        this.setState({ pipWindow: null, pipActive: false });
                        return true;
                    }
                    pipWindow = await docPip.requestWindow({
                        width: 480,
                        height: 340,
                    });
                }
                catch (e) {
                    console.warn('[PiP] documentPictureInPicture.requestWindow failed:', e);
                    pipWindow = null;
                }
            }
            if (!pipWindow) {
                // Attempt to notify parent window (e.g. ibc / VmeetView) via postMessage
                try {
                    if (window.parent && window.parent !== window.self) {
                        window.parent.postMessage({
                            mType: 'iisight',
                            message: 'requestPictureInPicture',
                        }, '*');
                    }
                }
                catch (e) { }
                return false;
            }
            globalPipWindow = pipWindow;
            const cleanup = () => {
                if (globalPipStoreUnsubscribe) {
                    globalPipStoreUnsubscribe();
                    globalPipStoreUnsubscribe = null;
                }
                try {
                    const targetNode = globalPipWindow?.document.getElementById('pip-root');
                    if (targetNode) {
                        ReactDOM.unmountComponentAtNode(targetNode);
                    }
                }
                catch (e) {
                    // ignore
                }
                globalPipWindow = null;
                this.setState({ pipWindow: null, pipActive: false });
            };
            globalPipWindow.addEventListener('pagehide', cleanup);
            globalPipWindow.addEventListener('beforeunload', cleanup);
            const updatePip = () => {
                if (!globalPipWindow || globalPipWindow.closed)
                    return;
                const doc = globalPipWindow.document;
                if (!doc)
                    return;
                const targetNode = doc.getElementById('pip-root') || doc.body;
                if (!targetNode)
                    return;
                const store = window.APP?.store;
                const state = store && typeof store.getState === 'function' ? store.getState() : null;
                const mediaState = state ? state['features/base/media'] : null;
                const participantsState = state ? state['features/base/participants'] : null;
                let remoteParticipants = [];
                if (participantsState?.remote) {
                    try {
                        remoteParticipants = Array.from(participantsState.remote.values());
                    }
                    catch (e) {
                        remoteParticipants = [];
                    }
                }
                else if (this.props?._remoteParticipants) {
                    remoteParticipants = this.props._remoteParticipants;
                }
                const localParticipant = participantsState?.local || this.props?._localParticipant;
                const tracks = (state ? state['features/base/tracks'] : null) || this.props?._tracks || [];
                const largeVideoParticipantId = state ? state['features/large-video']?.participantId : this.props?._largeVideoParticipantId;
                const audioMuted = state ? isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO) : this.props._audioMuted;
                const videoMuted = state ? isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO) : this.props._videoMuted;
                const props = {
                    _audioMuted: audioMuted,
                    _videoMuted: videoMuted,
                    _localParticipant: localParticipant,
                    _remoteParticipants: remoteParticipants,
                    _tracks: tracks,
                    _largeVideoParticipantId: largeVideoParticipantId,
                    dispatch: store ? store.dispatch : this.props.dispatch,
                    pipWindow: globalPipWindow,
                };
                try {
                    if (store) {
                        ReactDOM.render(React.createElement(Provider, { store: store },
                            React.createElement(PipErrorBoundary, null,
                                React.createElement(PipWindowContent, { ...props }))), targetNode);
                    }
                    else {
                        ReactDOM.render(React.createElement(PipErrorBoundary, null,
                            React.createElement(PipWindowContent, { ...props })), targetNode);
                    }
                }
                catch (e) {
                    console.error('[PiP] Error rendering to PiP window:', e);
                }
            };
            const setupPipDoc = () => {
                if (!globalPipWindow || globalPipWindow.closed)
                    return;
                const doc = globalPipWindow.document;
                if (!doc)
                    return;
                try {
                    doc.title = '11sight - Picture in Picture';
                }
                catch (e) {
                    // ignore
                }
                // Copy parent document stylesheets into PiP window for fonts/icons
                try {
                    Array.from(document.styleSheets).forEach((styleSheet) => {
                        try {
                            if (styleSheet.cssRules) {
                                const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
                                const style = doc.createElement('style');
                                style.textContent = cssRules;
                                doc.head.appendChild(style);
                            }
                        }
                        catch (e) {
                            if (styleSheet.href) {
                                const link = doc.createElement('link');
                                link.rel = 'stylesheet';
                                link.type = styleSheet.type || 'text/css';
                                link.href = styleSheet.href;
                                doc.head.appendChild(link);
                            }
                        }
                    });
                }
                catch (e) {
                    console.warn('[PiP] Error copying stylesheets:', e);
                }
                // Inject clean custom CSS for PiP window layout
                try {
                    let style = doc.getElementById('pip-base-style');
                    if (!style) {
                        style = doc.createElement('style');
                        style.id = 'pip-base-style';
                        style.textContent = `
              * { box-sizing: border-box; margin: 0; padding: 0; }
              html, body { background-color: #000000; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; overflow: hidden; width: 100vw; height: 100vh; }
              #pip-root { width: 100vw; height: 100vh; position: relative; display: flex; flex-direction: column; overflow: hidden; }
              button { cursor: pointer; border: none; outline: none; }
              button:hover { opacity: 0.85; transform: scale(1.05); }
              button:active { transform: scale(0.95); }
            `;
                        doc.head.appendChild(style);
                    }
                }
                catch (e) {
                    // ignore
                }
                let container = doc.getElementById('pip-root');
                if (!container && doc.body) {
                    container = doc.createElement('div');
                    container.id = 'pip-root';
                    doc.body.appendChild(container);
                }
                updatePip();
            };
            setupPipDoc();
            setTimeout(setupPipDoc, 50);
            // Subscribe to Redux store updates so PiP window stays rendered even if toolbar button unmounts
            const store = window.APP?.store;
            if (store && typeof store.subscribe === 'function') {
                if (globalPipStoreUnsubscribe) {
                    globalPipStoreUnsubscribe();
                }
                globalPipStoreUnsubscribe = store.subscribe(() => {
                    updatePip();
                });
            }
            this.setState({ pipWindow: globalPipWindow, pipActive: true });
            return true;
        }
        catch (err) {
            console.error('[PiP] Picture-in-Picture window error:', err);
            return false;
        }
    }
    _findRemoteVideoElement(remoteParticipantId) {
        const store = window.APP?.store;
        const state = store?.getState?.();
        const largeVideoId = state ? state['features/large-video']?.participantId : this.props._largeVideoParticipantId;
        const localId = state ? state['features/base/participants']?.local?.id : this.props._localParticipant?.id;
        const isVideoElementActive = (v) => {
            if (!v)
                return false;
            if (v.readyState < 1 || v.videoWidth === 0 || v.paused)
                return false;
            if (v.style.display === 'none' || v.style.visibility === 'hidden')
                return false;
            if (v.offsetParent === null && v.offsetWidth === 0 && v.offsetHeight === 0)
                return false;
            if (v.srcObject instanceof MediaStream) {
                const vTracks = v.srcObject.getVideoTracks();
                if (vTracks.length > 0 && vTracks.every((t) => t.readyState === 'ended')) {
                    return false;
                }
            }
            return true;
        };
        // 1. If largeVideo is displaying a remote participant, use it:
        const largeVideo = document.getElementById('largeVideo');
        if (largeVideo && largeVideoId && largeVideoId !== localId && isVideoElementActive(largeVideo)) {
            return largeVideo;
        }
        // 2. Look for remote video in filmstrip or tile view:
        if (remoteParticipantId) {
            const candidates = [
                document.getElementById(`participant_${remoteParticipantId}`),
                document.getElementById(`remoteVideo_${remoteParticipantId}`),
                document.querySelector(`[id*="${remoteParticipantId}"]`),
            ];
            for (const el of candidates) {
                if (el instanceof HTMLVideoElement && isVideoElementActive(el)) {
                    return el;
                }
                const v = el?.querySelector('video');
                if (v && isVideoElementActive(v)) {
                    return v;
                }
            }
        }
        // 3. Any video element starting with remoteVideo_:
        const remoteVideos = Array.from(document.querySelectorAll('video[id^="remoteVideo_"]'));
        for (const v of remoteVideos) {
            if (isVideoElementActive(v)) {
                return v;
            }
        }
        // 4. Any non-local playing video in DOM:
        const allVideos = Array.from(document.querySelectorAll('video')).filter((v) => v !== globalPipVideo && v !== globalCompositeLocalVideo && v !== globalCompositeRemoteVideo);
        const remoteVideo = allVideos.find((v) => {
            const isLocal = v.id?.includes('local') || Boolean(v.closest('#localVideoContainer, .local-video, #localVideo, [id*="localVideo"]'));
            return !isLocal && isVideoElementActive(v);
        });
        return remoteVideo || null;
    }
    _findLocalVideoElement() {
        const isVideoElementActive = (v) => {
            if (!v)
                return false;
            if (v.readyState < 1 || v.videoWidth === 0 || v.paused)
                return false;
            if (v.style.display === 'none' || v.style.visibility === 'hidden')
                return false;
            if (v.offsetParent === null && v.offsetWidth === 0 && v.offsetHeight === 0)
                return false;
            return true;
        };
        // 1. Direct localVideo or localVideo_container element:
        const ids = ['localVideo_container', 'localVideo', 'localScreenshare_container'];
        for (const id of ids) {
            const el = document.getElementById(id);
            if (el instanceof HTMLVideoElement && isVideoElementActive(el)) {
                return el;
            }
            if (el) {
                const v = el.querySelector('video');
                if (v && isVideoElementActive(v)) {
                    return v;
                }
            }
        }
        // 2. Any local video in DOM:
        const allVideos = Array.from(document.querySelectorAll('video')).filter((v) => v !== globalPipVideo && v !== globalCompositeLocalVideo && v !== globalCompositeRemoteVideo);
        const localVideo = allVideos.find((v) => {
            const isLocal = v.id?.includes('local') || Boolean(v.closest('#localVideoContainer, .local-video, #localVideo, [id*="localVideo"]'));
            return isLocal && isVideoElementActive(v);
        });
        return localVideo || null;
    }
    _ensureCompositeVideoAttached(v) {
        if (!v.parentElement) {
            v.style.position = 'fixed';
            v.style.bottom = '0px';
            v.style.right = '0px';
            v.style.width = '10px';
            v.style.height = '10px';
            v.style.opacity = '0.001';
            v.style.pointerEvents = 'none';
            v.style.zIndex = '-9999';
            document.body.appendChild(v);
        }
    }
    async _startCompositePip() {
        this._initPipVideo();
        globalIsCanvasCompositeRunning = true;
        if (!globalCompositeRemoteVideo) {
            globalCompositeRemoteVideo = document.createElement('video');
            globalCompositeRemoteVideo.muted = true;
            globalCompositeRemoteVideo.autoplay = true;
            globalCompositeRemoteVideo.playsInline = true;
        }
        this._ensureCompositeVideoAttached(globalCompositeRemoteVideo);
        if (!globalCompositeLocalVideo) {
            globalCompositeLocalVideo = document.createElement('video');
            globalCompositeLocalVideo.muted = true;
            globalCompositeLocalVideo.autoplay = true;
            globalCompositeLocalVideo.playsInline = true;
        }
        this._ensureCompositeVideoAttached(globalCompositeLocalVideo);
        const canvas = globalPipCanvas;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        const draw = () => {
            if (!globalIsCanvasCompositeRunning) {
                return;
            }
            const store = window.APP?.store;
            const state = store?.getState?.();
            const tracks = (state ? state['features/base/tracks'] : null) || this.props._tracks || [];
            const mediaState = state ? state['features/base/media'] : null;
            const participantsState = state ? state['features/base/participants'] : null;
            const largeVideoId = state ? state['features/large-video']?.participantId : this.props._largeVideoParticipantId;
            let remoteParticipants = [];
            if (participantsState?.remote) {
                try {
                    remoteParticipants = Array.from(participantsState.remote.values());
                }
                catch (e) {
                    remoteParticipants = [];
                }
            }
            else if (this.props._remoteParticipants) {
                remoteParticipants = this.props._remoteParticipants;
            }
            const realLargeVideoOwnerId = largeVideoId ? largeVideoId.split('-')[0] : null;
            const realRemoteParticipants = remoteParticipants.filter((p) => !p.fakeParticipant || p.fakeParticipant !== 'remote-screenshare');
            const remoteParticipant = (realLargeVideoOwnerId ? realRemoteParticipants.find((p) => p?.id === realLargeVideoOwnerId) : null) ||
                remoteParticipants.find((p) => p?.id === largeVideoId) ||
                realRemoteParticipants[0] ||
                remoteParticipants[0];
            const hasRemoteParticipant = Boolean(remoteParticipant);
            // 1. Resolve active remote track (prioritizing screen share)
            const screenShareTrack = tracks.find((t) => Boolean(t?.jitsiTrack) &&
                !t.local &&
                !t.muted &&
                (t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === 'desktop') &&
                (!remoteParticipant || t.participantId === remoteParticipant.id || t.participantId === realLargeVideoOwnerId)) || tracks.find((t) => Boolean(t?.jitsiTrack) &&
                !t.local &&
                !t.muted &&
                (t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === 'desktop'));
            let activeRemoteTrack = null;
            if (screenShareTrack) {
                activeRemoteTrack = screenShareTrack;
            }
            else if (remoteParticipant) {
                // 1. Try finding video track for remoteParticipant.id
                activeRemoteTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, remoteParticipant.id);
                // 2. Try realLargeVideoOwnerId if different
                if ((!activeRemoteTrack || activeRemoteTrack.muted) && realLargeVideoOwnerId && realLargeVideoOwnerId !== remoteParticipant.id) {
                    activeRemoteTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, realLargeVideoOwnerId);
                }
                // 3. Fallback to any non-local active camera video track
                if (!activeRemoteTrack || activeRemoteTrack.muted) {
                    const anyCameraTrack = tracks.find((t) => Boolean(t?.jitsiTrack) &&
                        !t.local &&
                        !t.muted &&
                        t.mediaType === MEDIA_TYPE.VIDEO &&
                        t.videoType !== 'desktop');
                    if (anyCameraTrack) {
                        activeRemoteTrack = anyCameraTrack;
                    }
                }
            }
            const isRemoteScreenShare = activeRemoteTrack?.videoType === 'desktop' ||
                activeRemoteTrack?.mediaType === MEDIA_TYPE.SCREENSHARE;
            const isRemoteTrackActive = Boolean(activeRemoteTrack) &&
                !activeRemoteTrack.muted &&
                (typeof activeRemoteTrack.jitsiTrack?.isMuted !== 'function' || !activeRemoteTrack.jitsiTrack.isMuted()) &&
                (isRemoteScreenShare ||
                    (remoteParticipant?.id ? !isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, remoteParticipant.id) : true) ||
                    (realLargeVideoOwnerId ? !isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, realLargeVideoOwnerId) : true));
            // Attach remote media stream to globalCompositeRemoteVideo if remote video track is active
            const remoteMediaStreamTrack = isRemoteTrackActive ? activeRemoteTrack?.jitsiTrack?.getTrack?.() : null;
            if (remoteMediaStreamTrack && globalCompositeRemoteVideo) {
                const currentTrack = globalCompositeRemoteVideo.srcObject?.getVideoTracks()?.[0];
                if (currentTrack !== remoteMediaStreamTrack || currentTrack?.readyState === 'ended') {
                    globalCompositeRemoteVideo.srcObject = new MediaStream([remoteMediaStreamTrack]);
                    globalCompositeRemoteVideo.play().catch(() => { });
                }
            }
            else if (!isRemoteTrackActive && globalCompositeRemoteVideo?.srcObject) {
                globalCompositeRemoteVideo.srcObject = null;
            }
            // 2. Resolve active local track
            const localParticipant = participantsState?.local || this.props._localParticipant;
            const activeLocalTrack = getLocalVideoTrack(tracks) ||
                (localParticipant?.id ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, localParticipant.id) : null) ||
                window.APP?.conference?.getLocalVideoTrack?.();
            const localMediaStreamTrack = activeLocalTrack?.jitsiTrack?.getTrack?.() || (activeLocalTrack?.getTrack ? activeLocalTrack.getTrack() : null);
            // Read real-time mute states from mediaState, fallback to tracks
            let isLocalMuted = false;
            if (mediaState?.video?.muted !== undefined) {
                isLocalMuted = Boolean(mediaState.video.muted);
            }
            else {
                isLocalMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
            }
            let isLocalAudioMuted = false;
            if (mediaState?.audio?.muted !== undefined) {
                isLocalAudioMuted = Boolean(mediaState.audio.muted);
            }
            else {
                isLocalAudioMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
            }
            if (localMediaStreamTrack && !isLocalMuted && globalCompositeLocalVideo) {
                const currentLocalTrack = globalCompositeLocalVideo.srcObject?.getVideoTracks()?.[0];
                if (currentLocalTrack !== localMediaStreamTrack) {
                    globalCompositeLocalVideo.srcObject = new MediaStream([localMediaStreamTrack]);
                    globalCompositeLocalVideo.play().catch(() => { });
                }
            }
            else if (isLocalMuted && globalCompositeLocalVideo?.srcObject) {
                globalCompositeLocalVideo.srcObject = null;
            }
            // Continuous sync of mediaSession device active status
            if ('mediaSession' in navigator) {
                if (typeof navigator.mediaSession.setMicrophoneActive === 'function') {
                    navigator.mediaSession.setMicrophoneActive(!isLocalAudioMuted);
                }
                if (typeof navigator.mediaSession.setCameraActive === 'function') {
                    navigator.mediaSession.setCameraActive(!isLocalMuted);
                }
            }
            if (ctx) {
                // Prioritize DOM video elements ONLY when remote track is active
                const domRemoteVideo = isRemoteTrackActive ? this._findRemoteVideoElement(remoteParticipant?.id) : null;
                const currentRemoteVideo = (domRemoteVideo && domRemoteVideo.readyState >= 1 && domRemoteVideo.videoWidth > 0 && !domRemoteVideo.paused)
                    ? domRemoteVideo
                    : (isRemoteTrackActive && globalCompositeRemoteVideo && globalCompositeRemoteVideo.readyState >= 1 && globalCompositeRemoteVideo.videoWidth > 0 && !globalCompositeRemoteVideo.paused)
                        ? globalCompositeRemoteVideo
                        : null;
                const domLocalVideo = this._findLocalVideoElement();
                const currentLocalVideo = (domLocalVideo && domLocalVideo.readyState >= 1 && domLocalVideo.videoWidth > 0 && !domLocalVideo.paused)
                    ? domLocalVideo
                    : (globalCompositeLocalVideo && globalCompositeLocalVideo.readyState >= 1 && globalCompositeLocalVideo.videoWidth > 0 && !globalCompositeLocalVideo.paused)
                        ? globalCompositeLocalVideo
                        : null;
                const remoteName = remoteParticipant?.name || remoteParticipant?.displayName || 'Remote Participant';
                const remoteInitials = getInitials(remoteName);
                const remoteBgColor = getAvatarColor(remoteInitials, []);
                const localName = localParticipant?.name || localParticipant?.displayName || 'Me';
                const localInitials = getInitials(localName);
                const localBgColor = getAvatarColor(localInitials, []);
                const canDrawLocalVideo = !isLocalMuted && Boolean(currentLocalVideo && currentLocalVideo.readyState >= 1 && currentLocalVideo.videoWidth > 0);
                if (hasRemoteParticipant) {
                    // ==========================================
                    // CASE 1: REMOTE PARTICIPANT PRESENT
                    // ==========================================
                    // 1. Draw Remote Video (or Screen Share) on Primary Stage
                    if (isRemoteTrackActive && currentRemoteVideo && currentRemoteVideo.readyState >= 1 && currentRemoteVideo.videoWidth > 0 && !currentRemoteVideo.paused) {
                        if (isRemoteScreenShare) {
                            ctx.fillStyle = '#000000';
                            ctx.fillRect(0, 0, w, h);
                            drawVideoAspect(ctx, currentRemoteVideo, 0, 0, w, h, 'contain');
                        }
                        else {
                            drawVideoAspect(ctx, currentRemoteVideo, 0, 0, w, h, 'cover');
                        }
                    }
                    else {
                        ctx.fillStyle = '#111827';
                        ctx.fillRect(0, 0, w, h);
                        const avatarRadius = 42;
                        const centerX = w / 2;
                        const centerY = h / 2 - 14;
                        ctx.fillStyle = remoteBgColor || '#10b981';
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, avatarRadius, 0, 2 * Math.PI);
                        ctx.fill();
                        ctx.fillStyle = '#ffffff';
                        ctx.font = 'bold 26px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(remoteInitials, centerX, centerY);
                        ctx.font = '600 15px sans-serif';
                        ctx.textBaseline = 'alphabetic';
                        ctx.fillText(remoteName, centerX, centerY + avatarRadius + 24);
                    }
                    // 2. Draw Local Video Inset (Top-Right Corner)
                    const insetW = 160;
                    const insetH = 96;
                    const insetX = w - insetW - 12;
                    const insetY = 12;
                    ctx.save();
                    drawRoundRect(ctx, insetX, insetY, insetW, insetH, 8);
                    ctx.fillStyle = '#111827';
                    ctx.fill();
                    ctx.clip();
                    if (canDrawLocalVideo && currentLocalVideo) {
                        // Mirror horizontally for self-view natural mirror effect
                        ctx.translate(insetX + insetW, insetY);
                        ctx.scale(-1, 1);
                        drawVideoAspect(ctx, currentLocalVideo, 0, 0, insetW, insetH, 'cover');
                    }
                    else {
                        const localRadius = 22;
                        const localX = insetX + insetW / 2;
                        const localY = insetY + insetH / 2 - 4;
                        ctx.fillStyle = localBgColor || '#6366f1';
                        ctx.beginPath();
                        ctx.arc(localX, localY, localRadius, 0, 2 * Math.PI);
                        ctx.fill();
                        ctx.fillStyle = '#ffffff';
                        ctx.font = 'bold 14px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(localInitials, localX, localY);
                    }
                    ctx.restore();
                    // Local Inset Border
                    ctx.save();
                    drawRoundRect(ctx, insetX, insetY, insetW, insetH, 8);
                    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
                    ctx.lineWidth = 1.5;
                    ctx.stroke();
                    ctx.restore();
                    // 'You' pill badge on local inset
                    ctx.save();
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                    drawRoundRect(ctx, insetX + 6, insetY + insetH - 22, 36, 16, 4);
                    ctx.fill();
                    ctx.fillStyle = '#ffffff';
                    ctx.font = 'bold 10px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText('You', insetX + 24, insetY + insetH - 14);
                    ctx.restore();
                    // 3. Remote Participant Name Badge (bottom-left)
                    ctx.save();
                    ctx.font = '500 12px sans-serif';
                    const nameText = remoteName;
                    const textWidth = ctx.measureText(nameText).width;
                    const pillW = Math.min(textWidth + 24, w - 24);
                    const pillH = 24;
                    const pillX = 12;
                    const pillY = h - pillH - 12;
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                    drawRoundRect(ctx, pillX, pillY, pillW, pillH, 4);
                    ctx.fill();
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(nameText, pillX + 12, pillY + pillH / 2, pillW - 20);
                    ctx.restore();
                }
                else {
                    // ==========================================
                    // CASE 2: SOLO USER (NO REMOTE PARTICIPANT)
                    // ==========================================
                    // Draw Local Video on Primary Stage
                    if (canDrawLocalVideo && currentLocalVideo) {
                        ctx.save();
                        ctx.translate(w, 0);
                        ctx.scale(-1, 1);
                        drawVideoAspect(ctx, currentLocalVideo, 0, 0, w, h, 'cover');
                        ctx.restore();
                    }
                    else {
                        ctx.fillStyle = '#111827';
                        ctx.fillRect(0, 0, w, h);
                        const avatarRadius = 42;
                        const centerX = w / 2;
                        const centerY = h / 2 - 14;
                        ctx.fillStyle = localBgColor || '#6366f1';
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, avatarRadius, 0, 2 * Math.PI);
                        ctx.fill();
                        ctx.fillStyle = '#ffffff';
                        ctx.font = 'bold 26px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(localInitials, centerX, centerY);
                        ctx.font = '600 15px sans-serif';
                        ctx.textBaseline = 'alphabetic';
                        ctx.fillText(localName, centerX, centerY + avatarRadius + 24);
                    }
                    // User Name Pill (bottom-left)
                    ctx.save();
                    ctx.font = '500 12px sans-serif';
                    const nameText = `${localName} (You)`;
                    const textWidth = ctx.measureText(nameText).width;
                    const pillW = Math.min(textWidth + 24, w - 24);
                    const pillH = 24;
                    const pillX = 12;
                    const pillY = h - pillH - 12;
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                    drawRoundRect(ctx, pillX, pillY, pillW, pillH, 4);
                    ctx.fill();
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(nameText, pillX + 12, pillY + pillH / 2, pillW - 20);
                    ctx.restore();
                }
            }
        };
        // Draw initial frame and start unthrottled Web Worker ticker
        draw();
        startTicker(draw);
        if (globalPipVideo && typeof globalPipVideo.requestPictureInPicture === 'function') {
            try {
                if (!globalPipVideo.srcObject) {
                    const stream = canvas.captureStream ? canvas.captureStream(30) : null;
                    if (stream) {
                        globalPipVideo.srcObject = stream;
                    }
                }
                try {
                    await globalPipVideo.play();
                }
                catch (e) {
                    // ignore
                }
                // Ensure video has loaded data and has non-zero dimensions
                if (globalPipVideo.readyState < 2 || globalPipVideo.videoWidth === 0) {
                    await new Promise((resolve) => {
                        let done = false;
                        const onReady = () => {
                            if (!done && globalPipVideo && globalPipVideo.readyState >= 2 && globalPipVideo.videoWidth > 0) {
                                done = true;
                                globalPipVideo.removeEventListener('loadeddata', onReady);
                                globalPipVideo.removeEventListener('canplay', onReady);
                                globalPipVideo.removeEventListener('playing', onReady);
                                resolve();
                            }
                        };
                        globalPipVideo?.addEventListener('loadeddata', onReady);
                        globalPipVideo?.addEventListener('canplay', onReady);
                        globalPipVideo?.addEventListener('playing', onReady);
                        setTimeout(() => {
                            if (!done) {
                                done = true;
                                resolve();
                            }
                        }, 150);
                    });
                }
                // Set up MediaSession action handlers BEFORE requesting PiP so Chrome includes conference buttons
                this._setupMediaSessionHandlers();
                await globalPipVideo.requestPictureInPicture();
                this._syncMediaSessionState();
                this.setState({ pipActive: true });
            }
            catch (pipErr) {
                console.error('[PiP] Composite Picture-in-Picture failed:', pipErr);
            }
        }
    }
    async _handleClick() {
        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
                this._stopCanvasComposite();
                this._clearMediaSessionHandlers();
                this.setState({ pipActive: false });
                return;
            }
            if (globalPipWindow && !globalPipWindow.closed) {
                globalPipWindow.close();
                globalPipWindow = null;
                if (globalPipStoreUnsubscribe) {
                    globalPipStoreUnsubscribe();
                    globalPipStoreUnsubscribe = null;
                }
                this.setState({ pipWindow: null, pipActive: false });
                return;
            }
            const docPip = this._getDocPipApi();
            if (docPip?.window) {
                docPip.window.close();
                this.setState({ pipWindow: null, pipActive: false });
                return;
            }
            // Try Document PiP first if available
            try {
                const opened = await this._openDocumentPip();
                if (opened)
                    return;
            }
            catch (e) {
                console.warn('[PiP] Document PiP failed, falling back to composite video PiP:', e);
            }
            await this._startCompositePip();
        }
        catch (error) {
            console.error('[PiP] Error toggling Picture in Picture:', error);
        }
    }
    render() {
        if (isMobileBrowser()) {
            return null;
        }
        return super.render();
    }
}
const mapStateToProps = (state) => {
    const mediaState = state?.['features/base/media'];
    const participantsState = state?.['features/base/participants'];
    const remoteParticipantsMap = participantsState?.remote;
    const remoteParticipants = remoteParticipantsMap ? Array.from(remoteParticipantsMap.values()) : [];
    const localParticipant = participantsState?.local;
    const tracks = state?.['features/base/tracks'] || [];
    const largeVideoParticipantId = state?.['features/large-video']?.participantId;
    return {
        _audioMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO),
        _videoMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO),
        _localParticipant: localParticipant,
        _remoteParticipants: remoteParticipants,
        _tracks: tracks,
        _largeVideoParticipantId: largeVideoParticipantId,
    };
};
export default translate(connect(mapStateToProps)(PictureInPictureButton));
